import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import IgnisIcon from '../components/icons/IgnisIcon';
import { useServedModel } from '../hooks/useServedModel';
import { PT_RISK_GRID, PT_GRID_COLS } from './portugalRiskGrid';

// ---------------------------------------------------------------------------
// Signature element: mainland Portugal rendered as the model's own risk grid,
// glowing on the dark ground. The footprint and the risk pattern are the real
// model output, downsampled offline from country.json (see portugalRiskGrid.ts).
// ---------------------------------------------------------------------------

type Risk = 'low' | 'moderate' | 'high' | 'extreme';

const RISK_FILL: Record<Risk, string> = {
  low: '#3E8E5A',
  moderate: '#EAB308',
  high: '#F26A4B',
  extreme: '#EF4444',
};

const LEVEL: Record<string, Risk> = {
  '0': 'low',
  '1': 'moderate',
  '2': 'high',
  '3': 'extreme',
};

function RiskGrid({ className = '' }: { className?: string }) {
  const cells: { col: number; row: number; risk: Risk }[] = [];
  PT_RISK_GRID.forEach((line, row) =>
    line.split('').forEach((ch, col) => {
      if (ch !== '.') cells.push({ col, row, risk: LEVEL[ch] });
    }),
  );

  const cols = PT_GRID_COLS;
  const rows = PT_RISK_GRID.length;
  const gap = 0.4;
  const size = 6;
  const step = size + gap;

  return (
    <svg
      className={className}
      viewBox={`0 0 ${cols * step} ${rows * step}`}
      role="img"
      aria-label="Mapa de risco de incêndio de Portugal continental, à escala de 1 km"
    >
      {cells.map(({ col, row, risk }) => {
        const hot = risk === 'high' || risk === 'extreme';
        return (
          <rect
            key={`${col}-${row}`}
            x={col * step}
            y={row * step}
            width={size}
            height={size}
            rx={1}
            fill={RISK_FILL[risk]}
            opacity={risk === 'low' ? 0.4 : risk === 'moderate' ? 0.85 : 1}
            style={hot ? { filter: `drop-shadow(0 0 1.4px ${RISK_FILL[risk]})` } : undefined}
            className={
              risk === 'extreme'
                ? 'motion-safe:animate-[pulse_2.6s_ease-in-out_infinite]'
                : undefined
            }
          />
        );
      })}
    </svg>
  );
}

function GridLegend() {
  const items: [Risk, string][] = [
    ['low', 'Reduzido'],
    ['moderate', 'Moderado'],
    ['high', 'Elevado'],
    ['extreme', 'Extremo'],
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 font-mono text-xs text-mist-muted">
      {items.map(([risk, label]) => (
        <span key={risk} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: RISK_FILL[risk] }} />
          {label}
        </span>
      ))}
    </div>
  );
}

// Shared little eyebrow label (mono, uppercase).
function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="mb-4 font-mono text-xs uppercase tracking-[0.28em] text-ember">{children}</p>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function Hero() {
  const stats = [
    ['88 527', 'células de 1 km'],
    ['24-72 h', 'de antecedência'],
    ['10', 'fontes de dados'],
    ['diário', 'atualizado'],
  ];

  return (
    <section className="relative overflow-hidden bg-ink-950">
      {/* faint grid texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'linear-gradient(#22302A 1px, transparent 1px), linear-gradient(90deg, #22302A 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 70% 30%, black, transparent)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 70% 30%, black, transparent)',
        }}
      />
      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-6 py-20 md:grid-cols-[1.1fr_0.9fr] md:py-28">
        {/* Left: thesis */}
        <div>
          <Eyebrow>Proteção ambiental orientada por modelos</Eyebrow>

          <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight text-mist sm:text-5xl">
            Machine learning ao serviço do território.
          </h1>

          <p className="mt-6 max-w-xl text-lg leading-relaxed text-mist-muted">
            A Tutela aplica aprendizagem automática a dados ambientais para antecipar risco e
            apoiar quem decide no terreno. O IGNIS prevê o risco de incêndio para todo o país,
            célula a célula. O LIMES prioriza a gestão das faixas de combustível, parcela a
            parcela, para os municípios.
          </p>

          <dl className="mt-8 flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs text-mist-faint">
            {stats.map(([v, k]) => (
              <div key={k} className="flex items-baseline gap-2">
                <dt className="text-base font-medium text-mist">{v}</dt>
                <dd>{k}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to="/ignis"
              className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-ember px-7 py-4 text-lg font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember"
            >
              <IgnisIcon className="h-5 w-5" />
              Explorar o IGNIS
            </Link>
            <Link
              to="/municipios"
              className="inline-flex items-center justify-center rounded-lg border border-ink-700 px-6 py-4 font-medium text-mist transition-colors hover:bg-ink-800"
            >
              Para municípios
            </Link>
          </div>
        </div>

        {/* Right: signature risk grid */}
        <div className="relative">
          <div className="rounded-2xl border border-ink-700 bg-ink-900/80 p-6 shadow-2xl backdrop-blur-sm sm:p-8">
            <RiskGrid className="mx-auto block h-[22rem] w-auto motion-safe:animate-[fadeIn_0.8s_ease-out]" />
            <div className="mt-6 border-t border-ink-700 pt-4">
              <p className="mb-2 font-mono text-xs uppercase tracking-wider text-mist-muted">
                Risco previsto · grelha de 1 km
              </p>
              <GridLegend />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Platform framing
// ---------------------------------------------------------------------------

function PlatformSection() {
  return (
    <section className="border-t border-ink-800 bg-ink-900 py-24">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <h2 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
          Uma plataforma, construída sobre dados.
        </h2>
        <p className="mt-6 text-lg leading-relaxed text-mist-muted">
          Cada módulo da Tutela resolve um problema ambiental concreto com o mesmo
          método: reunir dados públicos de qualidade, treinar modelos que os
          interpretam, e devolver informação acionável a comunidades e autoridades.
        </p>
        <p className="mt-4 text-lg leading-relaxed text-mist-muted">
          Dois produtos partilham esse método hoje.
        </p>
      </div>
      <div className="mx-auto mt-14 grid max-w-5xl gap-6 px-6 md:grid-cols-2">
        <Link
          to="/ignis"
          className="group rounded-2xl border border-ink-700 bg-ink-950 p-8 transition-colors hover:border-ember/50"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-3 py-1 font-mono text-xs font-semibold text-ember">
            <IgnisIcon className="h-3.5 w-3.5" />
            IGNIS
          </div>
          <h3 className="mt-4 font-display text-2xl font-bold text-mist">Risco de incêndio, célula a célula</h3>
          <p className="mt-3 text-mist-muted">
            Previsão diária a 24-72 horas para toda a grelha de 1 km de Portugal continental,
            validada publicamente contra os fogos reais. Gratuito para todos; briefing e consola
            municipal para as câmaras.
          </p>
          <span className="mt-5 inline-block font-mono text-xs uppercase tracking-widest text-ember group-hover:brightness-110">
            Explorar o IGNIS
          </span>
        </Link>
        <Link
          to="/limes"
          className="group rounded-2xl border border-ink-700 bg-ink-950 p-8 transition-colors hover:border-ember/50"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-3 py-1 font-mono text-xs font-semibold text-ember">
            LIMES
          </div>
          <h3 className="mt-4 font-display text-2xl font-bold text-mist">Faixas de combustível, parcela a parcela</h3>
          <p className="mt-3 text-mist-muted">
            A rede secundária de um concelho lida a partir do LiDAR nacional, dos ortofotomapas e do
            cadastro: uma lista ordenada de situações a verificar, com evidência por parcela. Para o
            GTF e a proteção civil municipal, após início de sessão.
          </p>
          <span className="mt-5 inline-block font-mono text-xs uppercase tracking-widest text-ember group-hover:brightness-110">
            Conhecer o LIMES
          </span>
        </Link>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// IGNIS module
// ---------------------------------------------------------------------------

function IgnisSection() {
  const { rebuiltServed } = useServedModel();
  const capabilities = [
    {
      title: 'Prever',
      body: 'Risco de incêndio a 24-72 horas, célula a célula, em toda a grelha nacional de 1 km.',
    },
    {
      title: 'Prevenir',
      body: 'Alertas por território, com tempo para agir antes da ignição.',
    },
    {
      title: 'Complementar',
      body: 'Desenhado para acompanhar o IPMA e o EFFIS à escala local, não para os substituir.',
    },
  ];

  return (
    <section className="border-t border-ink-800 bg-ink-950 py-24">
      <div className="mx-auto max-w-6xl px-6">
        <div className="max-w-2xl">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-sm font-semibold text-ember">
            <IgnisIcon className="h-4 w-4" />
            IGNIS
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
            Previsão de risco de incêndio para Portugal.
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-mist-muted">
            {rebuiltServed
              ? 'Um modelo de aprendizagem automática ordena o risco de ignição em todo o território continental, quadrícula a quadrícula, com um mapa novo todas as manhãs. Parte do histórico de ignições do ICNF por quadrícula e mês e aprende o desvio com a meteorologia do dia (IFS e Copernicus) e as características do terreno.'
              : 'Um ensemble de aprendizagem automática estima o risco de incêndio para todo o território continental, atualizado diariamente. Combina o histórico do ICNF, deteções de satélite da NASA FIRMS, meteorologia do IPMA e do Copernicus, e características de terreno e vegetação. Um modelo reconstruído em 2026 corre em sombra, lado a lado, e só entra no mapa se passar o portão público descrito em Modelos.'}
          </p>
        </div>

        <div className="mt-14 grid gap-px overflow-hidden rounded-xl border border-ink-700 bg-ink-700 md:grid-cols-3">
          {capabilities.map((c) => (
            <div key={c.title} className="bg-ink-900 p-8">
              <h3 className="font-display text-xl font-bold text-mist">{c.title}</h3>
              <p className="mt-3 text-mist-muted">{c.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <Link
            to="/ignis"
            className="inline-flex items-center gap-2.5 rounded-lg bg-ember px-7 py-4 text-lg font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember"
          >
            <IgnisIcon className="h-5 w-5" />
            Explorar o IGNIS
          </Link>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Why it's different (framed only in terms of what Tutela does)
// ---------------------------------------------------------------------------

function WhyDifferentSection() {
  const points = [
    {
      title: 'Ao nível da sua zona',
      body: 'O risco é calculado quadrícula a quadrícula, a cada 1 km, e não por distrito. Mostra diferenças dentro do mesmo concelho, onde o aviso geral não chega.',
    },
    {
      title: 'Muitas fontes, uma só leitura',
      body: 'Juntamos histórico de fogos, satélite, meteorologia, terreno e presença humana num único nível de risco, em vez de um só número solto.',
    },
    {
      title: 'Aprende com Portugal',
      body: 'Foi construída a partir de décadas de fogos reais em território português. Não é um modelo genérico apontado ao país à última hora.',
    },
    {
      title: 'Honesta sobre o que faz',
      body: 'Trabalha ao lado dos sistemas oficiais e é avaliada contra o que realmente ardeu. Dá uma estimativa útil, não certezas que ninguém tem.',
    },
  ];

  return (
    <section className="border-t border-ink-800 bg-ink-900 py-24">
      <div className="mx-auto max-w-5xl px-6">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
            Não é mais um mapa de fogos.
          </h2>
          <p className="mt-4 text-lg text-mist-muted">
            É uma leitura do risco pensada para quem tem de decidir no terreno.
          </p>
        </div>

        <div className="mt-14 grid gap-x-12 gap-y-10 sm:grid-cols-2">
          {points.map((p) => (
            <div key={p.title} className="border-l-2 border-ember/50 pl-5">
              <h3 className="font-display text-xl font-bold text-mist">{p.title}</h3>
              <p className="mt-2 text-mist-muted">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// How it works: plain language for non-technical visitors
// ---------------------------------------------------------------------------

function HowItWorksSection() {
  const steps = [
    {
      k: 'Dados',
      body: 'Reunimos dados públicos de todo o país: o que já ardeu, a meteorologia, a secura, a vegetação e o terreno.',
    },
    {
      k: 'Aprendizagem',
      body: 'O sistema estudou décadas de fogos reais em Portugal e aprendeu que condições costumam surgir antes de arder.',
    },
    {
      k: 'Risco',
      body: 'Traduz tudo isto num nível de risco para cada quadrícula de 1 km, atualizado todos os dias.',
    },
  ];

  const sources = [
    'Histórico de fogos e área ardida (ICNF)',
    'Atlas de severidade dNBR (1984-2022)',
    'Deteções de satélite (NASA FIRMS)',
    'Reanálise meteorológica (Copernicus ERA5, CERRA)',
    'Meteorologia (IPMA)',
    'Temperatura de superfície (MODIS)',
    'Índices de perigo meteorológico (sistema FWI) e de seca',
    'Ocupação do solo e coberto (CORINE, altura do coberto)',
    'Terreno (elevação, declive, exposição, microtopografia)',
    'Pressão humana (população, interface urbano-florestal, infraestruturas)',
  ];

  return (
    <section id="como-funciona" className="scroll-mt-20 border-t border-ink-800 bg-ink-950 py-24">
      <div className="mx-auto max-w-5xl px-6">
        <h2 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
          Dos dados ao risco.
        </h2>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-mist-muted">
          A Tutela aprendeu com o passado. Para cada dia e cada lugar de Portugal, comparou as
          condições (o tempo, a secura, a vegetação, o terreno e o histórico) com o que realmente
          ardeu, e reconheceu os sinais que costumam vir antes de um incêndio. Depois aplica esse
          conhecimento aos próximos dias.
        </p>

        <ol className="mt-14 grid gap-8 md:grid-cols-3">
          {steps.map((s, i) => (
            <li key={s.k} className="relative">
              <div className="font-mono text-sm font-semibold text-ember">
                {String(i + 1).padStart(2, '0')}
              </div>
              <h3 className="mt-2 font-display text-2xl font-bold text-mist">{s.k}</h3>
              <p className="mt-3 text-mist-muted">{s.body}</p>
            </li>
          ))}
        </ol>

        <div className="mt-16 border-t border-ink-800 pt-10">
          <p className="mb-5 font-mono text-xs uppercase tracking-[0.24em] text-mist-faint">
            Fontes de dados
          </p>
          <ul className="flex flex-wrap gap-2.5">
            {sources.map((src) => (
              <li
                key={src}
                className="rounded-md border border-ink-700 bg-ink-800 px-4 py-2 font-mono text-xs text-mist-muted"
              >
                {src}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Municipal band: the B2G offer in one breath, pointing at /municipios
// ---------------------------------------------------------------------------

function MunicipiosBand() {
  return (
    <section className="border-t border-ink-800 bg-ink-900 py-20">
      <div className="mx-auto flex max-w-5xl flex-col gap-8 px-6 md:flex-row md:items-center md:justify-between">
        <div className="max-w-2xl">
          <Eyebrow>Para municípios</Eyebrow>
          <h2 className="font-display text-3xl font-bold tracking-tight text-mist">
            O índice oficial diz o dia. Nós dizemos o sítio.
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-mist-muted">
            Briefing diário de risco por concelho, consola municipal por freguesia e priorização das
            faixas de gestão de combustível, ao serviço dos deveres do decreto-lei 82/2021. Contas por
            entidade; o mapa nacional continua público.
          </p>
        </div>
        <Link
          to="/municipios"
          className="inline-flex shrink-0 items-center justify-center rounded-lg bg-ember px-7 py-4 text-lg font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110"
        >
          Ver os serviços
        </Link>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

export default function Landing() {
  return (
    <div className="overflow-hidden bg-ink-950">
      <Hero />
      <PlatformSection />
      <IgnisSection />
      <WhyDifferentSection />
      <HowItWorksSection />
      <MunicipiosBand />
    </div>
  );
}
